import { useCallback, useSyncExternalStore } from 'react';
import type { ConversationId } from '@cyphex/types';
import { getRealtime } from '../api';
import { TYPING_THROTTLE_MS } from '../constants';
import { typingStore } from '../store/typingStore';
import type { TypingEntry } from '../types';

const EMPTY: readonly TypingEntry[] = [];
const lastSentAt = new Map<string, number>();

interface UseTypingResult {
  others: readonly TypingEntry[];
  /** Call on every keystroke — the throttle is inside. */
  notify: () => void;
  stop: () => void;
}

/**
 * Typing is the highest-frequency signal in chat, so it is throttled to one
 * frame per 3s per conversation (the gateway rate-limits to the same number)
 * and remote indicators are coalesced by the store before touching React.
 */
export function useTyping(conversationId: ConversationId | null): UseTypingResult {
  const subscribe = useCallback((listener: () => void) => typingStore.subscribe(listener), []);
  const snapshot = useCallback(
    () => (conversationId ? typingStore.select(conversationId) : EMPTY),
    [conversationId],
  );
  const others = useSyncExternalStore(subscribe, snapshot, snapshot);

  const notify = useCallback(() => {
    if (!conversationId) return;
    const now = Date.now();
    if (now - (lastSentAt.get(conversationId) ?? 0) < TYPING_THROTTLE_MS) return;
    lastSentAt.set(conversationId, now);
    getRealtime().typing(conversationId, 'start');
  }, [conversationId]);

  const stop = useCallback(() => {
    if (!conversationId) return;
    lastSentAt.delete(conversationId);
    getRealtime().typing(conversationId, 'stop');
  }, [conversationId]);

  return { others, notify, stop };
}
